import React from 'react'

import type { SortingState, Updater } from '@tanstack/react-table'
import __unionBy from 'lodash/unionBy'

import type { CommonAxiosPayload } from '@services/types'

import { cancellablePromise, getSortingParam, parseApiErrorMessage } from '@helpers'

import type { SimpleTableOnSelectionChange } from '@components/tables/SimpleTable/SimpleTable'

import { ATTATCH_SEARCH_ROWS_PER_PAGE } from '@constants'

import { useCancellablePromiseRef } from './useCancellablePromiseRef'
import { useNotVisibleSelectionCount } from './useNotVisibleSelectionCount'
import type { PageChangePayload } from './useTableControls'

export type AttachInvoiceBaseFilters = Partial<AmountFilterProps> & {
  search: string
  searchFields: string[]
}

export interface MultiAttachTableProps<ListData> {
  attachedItems?: SelectedItemProps[]
  companyId: number
  filters: AttachInvoiceBaseFilters
  isValid?: boolean
  loadData: AsyncFunction<
    {
      pageSize: number
      ordering: string
    } & AttachInvoiceBaseFilters,
    {
      data: ListData[]
      next: BackendCallableLink
      previous: BackendCallableLink
    }
  >
  loadDataByUrl: AsyncFunction<
    CommonAxiosPayload,
    {
      results: ListData[]
      next: BackendCallableLink
      previous: BackendCallableLink
    }
  >
  onSelect: (items: ListData[]) => void
  selectedItems: ListData[]
}

/**
 * Custom hook to manage the state and controls for an attach table.
 *
 * @template ListData - The type of the list data items.
 * @template AttachTableRow - The type of the attach table row.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {SelectedItemProps[]} params.defaultSelectedItems - The default selected items.
 * @param {Object} params.filters - Filters to apply when loading data.
 * @param {Function} params.loadData - Function to load data with given parameters.
 * @param {Function} params.loadDataByUrl - Function to load data by URL.
 * @param {Function} params.onSelect - Callback function when an item is selected.
 * @param {ListData[]} params.selectedItems - The currently selected items.
 * @param {string} rowDataAccessor - The accessor for the row data in the table row.
 * @param {string} defaultOrderBy - The default column to order by.
 *
 * @returns {Array} - Returns an array containing:
 *   - state: The current state of the table data, loading status, and pagination URLs.
 *   - sorting: The props to pass to the SimpleTable component.
 *   - onSortingChange: Function to handle sorting changes.
 *   - onPageChange: Function to handle page changes.
 *   - onSelectionChange: Function to handle selection changes.
 *   - notVisibleSelectedNum: Number of selected items not visible in the current page.
 *   - handleNotificationClear: Function to clear notifications.
 */
export function useAttachTableControls<ListData extends SelectedItemProps>(
  {
    filters,
    isValid = true,
    loadData,
    loadDataByUrl,
    onSelect,
    selectedItems,
  }: Pick<
    MultiAttachTableProps<ListData>,
    'isValid' | 'loadData' | 'loadDataByUrl' | 'filters' | 'onSelect' | 'selectedItems'
  >,
  defaultOrderBy: string
) {
  const cPromiseRef = useCancellablePromiseRef()
  const [state, setState] = React.useState<{
    data: ListData[]
    error: Nullable<string>
    loading: boolean
    nextPageUrl: Nullable<string>
    previousPageUrl: Nullable<string>
  }>({
    data: [],
    error: null,
    loading: false,
    nextPageUrl: null,
    previousPageUrl: null,
  })
  const [sorting, setSorting] = React.useState<SortingState>([{ id: defaultOrderBy, desc: true }])

  const onSortingChange = React.useCallback(
    (updaterOrValue: Updater<SortingState>) => {
      const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue

      setSorting(newSorting)
    },
    [sorting]
  )

  const onPageChange = React.useCallback(
    async ({ url }: PageChangePayload) => {
      try {
        setState(state => ({ ...state, loading: true }))
        cPromiseRef.current = cancellablePromise(loadDataByUrl({ method: 'get', url }))
        const { data, next, previous } = await cPromiseRef.current.promise
        setState(state => ({
          ...state,
          data,
          nextPageUrl: next,
          previousPageUrl: previous,
          loading: false,
        }))
      } catch (error) {
        const errorMsg = parseApiErrorMessage(error)
        if (errorMsg) {
          setState(state => ({ ...state, loading: false, error: errorMsg }))
        }
      }
    },
    [cPromiseRef, loadDataByUrl]
  )

  const onSelectionChange: SimpleTableOnSelectionChange = React.useCallback(
    selectedRowIds => {
      onSelect(__unionBy(state.data, selectedItems, 'id').filter(({ id }) => selectedRowIds[id]))
    },
    [state.data, onSelect, selectedItems]
  )

  //* non visible selection control
  const notVisibleSelectedNum = useNotVisibleSelectionCount(selectedItems, state.data)

  const handleNotificationClear = React.useCallback(() => {
    const dataIds = state.data.map(obj => obj.id)

    onSelect(selectedItems.filter(sData => dataIds.includes(sData.id)))
  }, [onSelect, selectedItems, state.data])

  React.useEffect(() => {
    async function fetchData() {
      const params = {
        pageSize: ATTATCH_SEARCH_ROWS_PER_PAGE,
        ordering: getSortingParam(sorting[0]),
        ...filters,
      }
      try {
        setState(state => ({ ...state, error: null, loading: true }))
        cPromiseRef.current = cancellablePromise(loadData(params))
        const { data, next, previous } = await cPromiseRef.current.promise
        setState(state => ({
          ...state,
          data,
          nextPageUrl: next,
          previousPageUrl: previous,
          loading: false,
        }))
      } catch (error) {
        const errorMsg = parseApiErrorMessage(error)
        if (errorMsg) {
          setState(state => ({ ...state, loading: false, error: errorMsg }))
        }
      }
    }

    if (isValid) {
      fetchData()
    }
  }, [cPromiseRef, filters, isValid, loadData, sorting])

  return [
    state,
    sorting,
    onSortingChange,
    onPageChange,
    onSelectionChange,
    notVisibleSelectedNum,
    handleNotificationClear,
  ] as const
}
